import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MbCard } from "@/components/MbCard";
import { PhoneInput } from "@/components/PhoneInput";
import { Loader2, Copy } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { AppFooter } from "@/components/AppFooter";

const COUNTRY_CODE = import.meta.env.VITE_COUNTRY_CODE || '+91';
const OTP_ENABLED = import.meta.env.VITE_ENABLE_OTP === 'true';

const Auth = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const { logo, strings } = useLanguage();
  
  // Login method toggle
  const [loginMethod, setLoginMethod] = useState<'phone' | 'email'>('phone');
  
  // Form fields
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showTempPassword, setShowTempPassword] = useState<string | null>(null);
  
  // Forgot password mode
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState<'phone' | 'otp' | 'success'>('phone');
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        checkUserRole(session.user.id);
      }
    });
  }, []);

  const checkUserRole = async (userId: string) => {
    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .maybeSingle();

    if (roleData?.role === "admin") {
      navigate("/admin/dashboard");
    } else {
      navigate("/today");
    }
  };

  const handleLogin = async () => {
    if (loginMethod === 'phone' && phone.length !== 10) {
      toast.error(strings.enterValidPhone);
      return;
    }

    if (loginMethod === 'email' && !email.includes('@')) {
      toast.error('કૃપા કરીને માન્ય ઇમેઇલ દાખલ કરો');
      return;
    }

    if (!password) {
      toast.error(strings.enterPassword);
      return;
    }

    setLoading(true);
    try {
      const credentials = loginMethod === 'phone'
        ? { phone: `${COUNTRY_CODE}${phone}`, password }
        : { email, password };

      const { data, error } = await supabase.auth.signInWithPassword(credentials);

      if (error) {
        if (error.message.includes('phone_provider_disabled')) {
          toast.error('ફોન લોગિન સક્રિય નથી. કૃપા કરીને એડમિનનો સંપર્ક કરો.');
        } else if (error.message.includes('Invalid login credentials')) {
          toast.error(loginMethod === 'phone' ? 'ખોટો ફોન નંબર અથવા પાસવર્ડ' : 'ખોટો ઇમેઇલ અથવા પાસવર્ડ');
        } else {
          toast.error('લોગિન નિષ્ફળ. કૃપા કરીને ફરી પ્રયાસ કરો.');
        }
        return;
      }

      toast.success(strings.loginSuccess);
      if (data.user) {
        await checkUserRole(data.user.id);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || strings.error);
    } finally {
      setLoading(false);
    }
  };

  const handleProvision = async () => {
    if (phone.length !== 10) {
      toast.error(strings.enterValidPhone);
      return;
    }

    if (!isLogin && !name.trim()) {
      toast.error(strings.enterName);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-provision`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ 
            phone, 
            name: name || undefined,
            role: 'subscriber'
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to create account');
      }

      // Show temp password to user
      setShowTempPassword(data.tempPassword);
      toast.success(strings.accountCreated);

      // Auto-login with temp password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        phone: `${COUNTRY_CODE}${phone}`,
        password: data.tempPassword,
      });

      if (signInError) {
        toast.error(strings.error);
        return;
      }

      toast.info(strings.changePasswordAfterLogin);
      await checkUserRole(data.auth_user_id);
    } catch (error: any) {
      console.error('Provision error:', error);
      toast.error(error.message || strings.error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(strings.passwordCopied);
  };

  const handleRequestPasswordReset = async () => {
    if (phone.length !== 10) {
      toast.error(strings.enterValidPhone);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-password-reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ phone }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send OTP');
      }

      toast.success(strings.otpSent);
      setResetStep('otp');
    } catch (error: any) {
      console.error('Password reset request error:', error);
      toast.error(error.message || strings.error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (otp.length !== 6) {
      toast.error(strings.enterValidOtp);
      return;
    }

    if (newPassword.length < 6) {
      toast.error(strings.passwordMinLength);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(strings.passwordMismatch);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ phone, otp, newPassword }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      toast.success(strings.passwordChanged);
      setResetStep('success');
      
      // Auto-login after 2 seconds
      setTimeout(async () => {
        await handleLogin();
      }, 2000);
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast.error(error.message || strings.error);
    } finally {
      setLoading(false);
    }
  };

  const resetForgotPasswordFlow = () => {
    setIsForgotPassword(false);
    setResetStep('phone');
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
  };

  if (OTP_ENABLED) {
    // Placeholder for old OTP UI - kept for rollback capability
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))] flex items-center justify-center p-4">
        <MbCard className="w-full max-w-md">
          <div className="p-8 text-center">
            <p className="text-muted-foreground">OTP મોડ સક્રિય છે. કૃપા કરીને ENABLE_OTP=false સેટ કરો.</p>
          </div>
        </MbCard>
      </div>
    );
  }

  // Forgot Password UI
  if (isForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))] flex items-center justify-center p-4">
        <MbCard className="w-full max-w-md">
          <div className="p-8 space-y-6">
            <div className="text-center space-y-4">
              <img src={logo} alt="દમયંતી બાગ" className="h-16 mx-auto" />
              <h1 className="text-2xl font-bold text-[hsl(var(--mb-green-dark))]">
                {strings.passwordReset}
              </h1>
              <p className="text-sm text-muted-foreground">
                {resetStep === 'phone' && strings.enterPhoneForReset}
                {resetStep === 'otp' && strings.enterOtpAndPassword}
                {resetStep === 'success' && strings.passwordResetSuccess}
              </p>
            </div>

            {resetStep === 'phone' && (
              <div className="space-y-4">
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  disabled={loading}
                />
                <Button
                  onClick={handleRequestPasswordReset}
                  disabled={loading || phone.length !== 10}
                  variant="hero"
                  className="w-full"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {strings.sendOTP}
                </Button>
              </div>
            )}

            {resetStep === 'otp' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">OTP</label>
                  <Input
                    type="text"
                    placeholder={strings.enterOtpPlaceholder}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={loading}
                    maxLength={6}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">{strings.newPassword}</label>
                  <Input
                    type="password"
                    placeholder={strings.enterNewPasswordPlaceholder}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">{strings.confirmPassword}</label>
                  <Input
                    type="password"
                    placeholder={strings.reEnterPasswordPlaceholder}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <Button
                  onClick={handleResetPassword}
                  disabled={loading || otp.length !== 6 || newPassword.length < 6 || newPassword !== confirmPassword}
                  variant="hero"
                  className="w-full"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {strings.changePassword}
                </Button>
              </div>
            )}

            {resetStep === 'success' && (
              <div className="text-center space-y-4">
                <div className="p-4 bg-[hsl(var(--mb-green-light))] rounded-lg">
                  <p className="text-sm">{strings.passwordResetSuccessful}</p>
                  <p className="text-xs text-muted-foreground mt-2">{strings.autoLoginSoon}</p>
                </div>
              </div>
            )}

            <div className="text-center">
              <button
                onClick={resetForgotPasswordFlow}
                className="text-sm text-primary hover:underline"
                disabled={loading}
              >
                {strings.backToLogin}
              </button>
            </div>
          </div>
        </MbCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))] flex items-center justify-center p-4">
      <MbCard className="w-full max-w-md">
        <div className="p-8 space-y-6">
          {/* Logo */}
          <div className="text-center space-y-4">
            <img src={logo} alt="દમયંતી બાગ" className="h-16 mx-auto" />
            <h1 className="text-2xl font-bold text-[hsl(var(--mb-green-dark))]">
              {isLogin ? strings.login : strings.signUp}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isLogin 
                ? strings.yourPhoneAndPassword
                : strings.createNewAccountDesc}
            </p>
          </div>

          {/* Show temp password if generated */}
          {showTempPassword && (
            <div className="p-4 bg-[hsl(var(--mb-green-light))] rounded-lg space-y-2">
              <p className="text-sm font-medium">{strings.yourTempPassword}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-background rounded text-xs break-all">
                  {showTempPassword}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(showTempPassword)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {strings.keepPasswordSafe}
              </p>
            </div>
          )}

          {/* Form */}
          <div className="space-y-4">
            {isLogin && (
              <div className="flex gap-2 p-1 bg-muted rounded-lg">
                <button
                  onClick={() => setLoginMethod('phone')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    loginMethod === 'phone'
                      ? 'bg-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  ફોન
                </button>
                <button
                  onClick={() => setLoginMethod('email')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    loginMethod === 'email'
                      ? 'bg-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  ઇમેઇલ
                </button>
              </div>
            )}

            {!isLogin && (
              <div className="space-y-2">
                <label className="text-sm font-medium">{strings.name}</label>
                <Input
                  type="text"
                  placeholder={strings.yourNamePlaceholder}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                />
              </div>
            )}

            {(loginMethod === 'phone' || !isLogin) && (
              <PhoneInput
                value={phone}
                onChange={setPhone}
                disabled={loading}
              />
            )}

            {loginMethod === 'email' && isLogin && (
              <div className="space-y-2">
                <label className="text-sm font-medium">ઇમેઇલ</label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
              </div>
            )}

            {isLogin && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">{strings.password}</label>
                  {loginMethod === 'phone' && (
                    <button
                      type="button"
                      onClick={() => setIsForgotPassword(true)}
                      className="text-xs text-primary hover:underline"
                    >
                      {strings.forgotPassword}
                    </button>
                  )}
                </div>
                <Input
                  type="password"
                  placeholder={strings.enterPasswordPlaceholder}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            )}

            {isLogin ? (
              <Button
                onClick={handleLogin}
                disabled={
                  loading || 
                  (loginMethod === 'phone' && phone.length !== 10) ||
                  (loginMethod === 'email' && !email.includes('@')) ||
                  !password
                }
                variant="hero"
                className="w-full"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {strings.login}
              </Button>
            ) : (
              <div className="space-y-2">
                <Button
                  onClick={handleProvision}
                  disabled={loading || phone.length !== 10 || !name}
                  variant="hero"
                  className="w-full"
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {strings.getTempPassword}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  {strings.clickToCreateAccount}
                </p>
              </div>
            )}
          </div>

          {/* Toggle Login/Signup */}
          <div className="text-center text-sm">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setPassword("");
                setShowTempPassword(null);
              }}
              className="text-primary hover:underline"
            >
              {isLogin ? strings.createNewAccount : strings.alreadyHaveAccount}
            </button>
          </div>

          {/* Back to Home */}
          <div className="text-center">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              disabled={loading}
            >
              {strings.backToHome}
            </Button>
          </div>
        </div>
      </MbCard>
      
      <AppFooter />
    </div>
  );
};

export default Auth;
